import React, { useEffect, useRef, useState, useCallback } from 'react'
import * as THREE from 'three'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'
import { fmtCoins } from '../../lib/cards'
import { io, Socket } from 'socket.io-client'
import { Sounds } from '../../lib/sounds'

// ─── Types ────────────────────────────────────────────────────────────────────
interface Props {
  token:string; balance:number|null
  playerId?:string; operatorId?:string; displayName?:string
}
type Phase = 'idle'|'rolling'|'result'
type Bet   = 'over'|'under'

// ─── Payout table (2 dice, sum 2-12) ─────────────────────────────────────────
// Over target T: win if sum > T
// Under target T: win if sum < T
// Target range: 3-9 (always fair odds)
const TARGETS  = [3,4,5,6,7,8,9]
const OVER_MULT: Record<number,number> = {3:1.09,4:1.15,5:1.28,6:1.5,7:2.0,8:3.0,9:6.0}
const UNDER_MULT:Record<number,number> = {3:6.0, 4:3.0, 5:2.0, 6:1.5,7:1.28,8:1.15,9:1.09}

// ─── Create wooden dice texture ───────────────────────────────────────────────
function makeDiceFaceTexture(value:number): THREE.CanvasTexture {
  const sz  = 256
  const cvs = document.createElement('canvas')
  cvs.width = sz; cvs.height = sz
  const ctx = cvs.getContext('2d')!

  // Wood gradient background
  const grad = ctx.createLinearGradient(0,0,sz,sz)
  grad.addColorStop(0,   '#e8a050')
  grad.addColorStop(0.3, '#d4872e')
  grad.addColorStop(0.6, '#f0a855')
  grad.addColorStop(1,   '#c07828')
  ctx.fillStyle = grad
  ctx.fillRect(0,0,sz,sz)

  // Wood grain lines
  ctx.strokeStyle = 'rgba(60,25,5,0.30)'
  ctx.lineWidth   = 2
  for(let i=0;i<8;i++){
    ctx.beginPath()
    ctx.moveTo(0, i*38+Math.random()*10)
    ctx.bezierCurveTo(sz/3,i*38-10,2*sz/3,i*38+10,sz,i*38+Math.random()*10)
    ctx.stroke()
  }

  // Rounded border
  ctx.strokeStyle = 'rgba(40,15,0,0.85)'
  ctx.lineWidth   = 14
  const r=28
  ctx.beginPath()
  ctx.moveTo(r,0);ctx.lineTo(sz-r,0);ctx.arcTo(sz,0,sz,r,r)
  ctx.lineTo(sz,sz-r);ctx.arcTo(sz,sz,sz-r,sz,r)
  ctx.lineTo(r,sz);ctx.arcTo(0,sz,0,sz-r,r)
  ctx.lineTo(0,r);ctx.arcTo(0,0,r,0,r);ctx.closePath()
  ctx.stroke()

  // Dot positions per face value
  const dotSets:Record<number,[number,number][]> = {
    1:[[0.5,0.5]],
    2:[[0.25,0.25],[0.75,0.75]],
    3:[[0.25,0.25],[0.5,0.5],[0.75,0.75]],
    4:[[0.25,0.25],[0.75,0.25],[0.25,0.75],[0.75,0.75]],
    5:[[0.25,0.25],[0.75,0.25],[0.5,0.5],[0.25,0.75],[0.75,0.75]],
    6:[[0.25,0.2],[0.75,0.2],[0.25,0.5],[0.75,0.5],[0.25,0.8],[0.75,0.8]],
  }
  const dots = dotSets[value] || []
  const dotR = 26

  dots.forEach(([fx,fy])=>{
    const x=fx*sz, y=fy*sz
    // Dot shadow
    ctx.beginPath(); ctx.arc(x+2,y+2,dotR,0,Math.PI*2)
    ctx.fillStyle='rgba(20,5,0,0.7)'; ctx.fill()
    // Dot body — ivory/cream
    const dg=ctx.createRadialGradient(x-5,y-5,2,x,y,dotR)
    dg.addColorStop(0,'#ffffff'); dg.addColorStop(1,'#f0e0c0')
    ctx.beginPath(); ctx.arc(x,y,dotR,0,Math.PI*2)
    ctx.fillStyle=dg; ctx.fill()
  })

  return new THREE.CanvasTexture(cvs)
}

// ─── Create felt table texture ─────────────────────────────────────────────
function makeFeltTexture(): THREE.CanvasTexture {
  const sz=512
  const cvs=document.createElement('canvas'); cvs.width=sz; cvs.height=sz
  const ctx=cvs.getContext('2d')!
  // Deep green felt
  const g=ctx.createRadialGradient(sz/2,sz/2,0,sz/2,sz/2,sz*0.7)
  g.addColorStop(0,'#1a6b3a'); g.addColorStop(0.5,'#145530'); g.addColorStop(1,'#0d3d22')
  ctx.fillStyle=g; ctx.fillRect(0,0,sz,sz)
  // Felt texture noise
  for(let i=0;i<6000;i++){
    const x=Math.random()*sz, y=Math.random()*sz
    ctx.fillStyle=`rgba(${Math.random()>0.5?255:0},${Math.random()>0.5?100:0},0,0.025)`
    ctx.fillRect(x,y,1,1)
  }
  return new THREE.CanvasTexture(cvs)
}

// ─── Dice face order for THREE.js BoxGeometry ─────────────────────────────────
// BoxGeometry face order: +X, -X, +Y, -Y, +Z, -Z
// Standard dice: 1 opposite 6, 2 opposite 5, 3 opposite 4
// +X=2, -X=5, +Y=1, -Y=6, +Z=3, -Z=4
const FACE_VALUES = [2,5,1,6,3,4]

function createDice(scene:THREE.Scene, x:number): THREE.Mesh {
  const geo  = new THREE.BoxGeometry(1,1,1)
  const mats = FACE_VALUES.map(v=>new THREE.MeshStandardMaterial({
    map:makeDiceFaceTexture(v), roughness:0.35, metalness:0.08
  }))
  const mesh = new THREE.Mesh(geo, mats)
  mesh.position.set(x,0.5,0)
  mesh.castShadow    = true
  mesh.receiveShadow = true
  scene.add(mesh)
  return mesh
}

// ─── Get rotation to show face value on top (+Y face) ────────────────────────
// We need to rotate so the desired value faces UP (+Y)
const FACE_ROTATIONS:Record<number,[number,number,number]> = {
  1: [0,0,0],             // 1 already on +Y
  6: [Math.PI,0,0],       // 6 opposite to 1
  2: [0,0,-Math.PI/2],    // 2 on +X → rotate to +Y
  5: [0,0,Math.PI/2],     // 5 on -X → rotate to +Y
  3: [Math.PI/2,0,0],     // 3 on +Z → rotate to +Y
  4: [-Math.PI/2,0,0],    // 4 on -Z → rotate to +Y
}

// ─── Main component ───────────────────────────────────────────────────────────
export function DiceGame({token,balance:initBalance,playerId:initPid,operatorId:initOid}:Props){
  const mountRef  = useRef<HTMLDivElement>(null)
  const socketRef = useRef<Socket|null>(null)
  const sessionRef= useRef({playerId:initPid||'',operatorId:initOid||''})
  const rendRef   = useRef<THREE.WebGLRenderer|null>(null)
  const sceneRef  = useRef<THREE.Scene|null>(null)
  const camRef    = useRef<THREE.PerspectiveCamera|null>(null)
  const dice1Ref  = useRef<THREE.Mesh|null>(null)
  const dice2Ref  = useRef<THREE.Mesh|null>(null)
  const rafRef    = useRef(0)
  const rollRef   = useRef<{
    active:boolean; t:number; dur:number
    d1Start:THREE.Euler; d2Start:THREE.Euler
    d1End:THREE.Euler;   d2End:THREE.Euler
    d1Bounce:number;     d2Bounce:number
  }>({active:false,t:0,dur:1.8,
    d1Start:new THREE.Euler(),d2Start:new THREE.Euler(),
    d1End:new THREE.Euler(),d2End:new THREE.Euler(),
    d1Bounce:0,d2Bounce:0})

  const [phase,setPhase]     = useState<Phase>('idle')
  const [balance,setBalance] = useState<number|null>(initBalance)
  const [stake,setStake]     = useState(20)
  const [target,setTarget]   = useState(7)
  const [bet,setBet]         = useState<Bet>('over')
  const [result,setResult]   = useState<{d1:number;d2:number;sum:number;win:boolean;payout:number}|null>(null)
  const [toast,setToast]     = useState('')
  const [history,setHistory] = useState<Array<{sum:number;win:boolean;payout:number}>>([])
  const [currency,setCurrency]= useState('coins')

  useEffect(()=>{ if(initBalance!==null) setBalance(initBalance) },[initBalance])
  const showToast=(msg:string,dur=2500)=>{ setToast(msg); setTimeout(()=>setToast(''),dur) }

  // ── Multiplier for current selection ─────────────────────────────────────
  const mult = bet==='over' ? OVER_MULT[target] : UNDER_MULT[target]
  const winChance = bet==='over'
    ? Math.round((Object.keys(OVER_MULT).length - TARGETS.indexOf(target)) / 11 * 100)
    : Math.round((TARGETS.indexOf(target)) / 11 * 100)

  // ── Three.js setup ────────────────────────────────────────────────────────
  useEffect(()=>{
    const el=mountRef.current; if(!el) return
    const W=el.clientWidth, H=el.clientHeight

    // Renderer
    const renderer=new THREE.WebGLRenderer({antialias:true,alpha:false})
    renderer.setSize(W,H)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio,2))
    renderer.shadowMap.enabled=true
    renderer.shadowMap.type=THREE.PCFSoftShadowMap
    renderer.setClearColor(0x0d1f12)
    el.appendChild(renderer.domElement)
    rendRef.current=renderer

    // Scene
    const scene=new THREE.Scene()
    scene.fog=new THREE.FogExp2(0x0d1f12,0.18)
    sceneRef.current=scene

    // Camera
    const cam=new THREE.PerspectiveCamera(50,W/H,0.1,100)
    cam.position.set(0,4.5,5); cam.lookAt(0,0,0)
    camRef.current=cam

    // Lights
    const ambient=new THREE.AmbientLight(0xffffff,1.2)
    scene.add(ambient)
    const spot=new THREE.SpotLight(0xffffff,5.0,20,Math.PI/4,0.3)
    spot.position.set(0,7,3); spot.castShadow=true
    spot.shadow.mapSize.set(1024,1024); spot.shadow.camera.near=1
    scene.add(spot)
    const fill=new THREE.DirectionalLight(0xfff0d0,1.2)
    fill.position.set(-4,3,2); scene.add(fill)
    const fill2=new THREE.DirectionalLight(0xd0e8ff,0.8)
    fill2.position.set(4,2,-2); scene.add(fill2)

    // Felt table
    const feltTex=makeFeltTexture()
    feltTex.wrapS=feltTex.wrapT=THREE.RepeatWrapping
    feltTex.repeat.set(4,4)
    const tableGeo=new THREE.CylinderGeometry(6,6.2,0.18,64)
    const tableMat=new THREE.MeshStandardMaterial({map:feltTex,roughness:0.95,metalness:0})
    const table=new THREE.Mesh(tableGeo,tableMat)
    table.receiveShadow=true; table.position.y=-0.09; scene.add(table)

    // Table rim (wood)
    const rimGeo=new THREE.TorusGeometry(6.1,0.22,12,64)
    const rimMat=new THREE.MeshStandardMaterial({color:0x7a4a1a,roughness:0.7,metalness:0.1})
    const rim=new THREE.Mesh(rimGeo,rimMat)
    rim.rotation.x=Math.PI/2; rim.position.y=0.02; scene.add(rim)

    // Dice
    dice1Ref.current=createDice(scene,-0.8)
    dice2Ref.current=createDice(scene, 0.8)

    // Resize
    const onResize=()=>{
      const w=el.clientWidth, h=el.clientHeight
      renderer.setSize(w,h); cam.aspect=w/h; cam.updateProjectionMatrix()
    }
    window.addEventListener('resize',onResize)

    // Render loop
    let last=0
    const loop=(t:number)=>{
      rafRef.current=requestAnimationFrame(loop)
      const dt=Math.min((t-last)/1000,0.05); last=t
      const r=rollRef.current

      if(r.active){
        r.t=Math.min(r.t+dt/r.dur,1)
        const p=r.t

        // Easing: fast roll → slow settle
        const eased=p<0.7 ? p/0.7 : 1-(1-(p-0.7)/0.3)**3

        if(dice1Ref.current&&dice2Ref.current){
          const d1=dice1Ref.current, d2=dice2Ref.current

          // Spin during roll (fast) then settle to final face
          const spinSpeed=Math.PI*8*(1-eased)
          d1.rotation.x=r.d1Start.x+(r.d1End.x-r.d1Start.x)*eased+spinSpeed*p
          d1.rotation.z=r.d1Start.z+(r.d1End.z-r.d1Start.z)*eased+spinSpeed*p*0.7
          d2.rotation.x=r.d2Start.x+(r.d2End.x-r.d2Start.x)*eased+spinSpeed*p*0.9
          d2.rotation.z=r.d2Start.z+(r.d2End.z-r.d2Start.z)*eased-spinSpeed*p*0.8

          // Bounce height
          const bounce1=Math.sin(p*Math.PI*4)*0.8*(1-eased)*r.d1Bounce
          const bounce2=Math.sin(p*Math.PI*3.5+0.4)*0.8*(1-eased)*r.d2Bounce
          d1.position.y=0.5+Math.max(0,bounce1)
          d2.position.y=0.5+Math.max(0,bounce2)

          // Drift slightly apart during roll
          const drift=Math.sin(p*Math.PI)*0.3
          d1.position.x=-0.8-drift
          d2.position.x= 0.8+drift
        }

        if(r.t>=1) r.active=false
      } else {
        // Idle gentle float
        if(dice1Ref.current) dice1Ref.current.position.y=0.5+Math.sin(t/1200)*0.03
        if(dice2Ref.current) dice2Ref.current.position.y=0.5+Math.sin(t/1000+1)*0.03
      }

      renderer.render(scene,cam)
    }
    rafRef.current=requestAnimationFrame(loop)

    return()=>{
      window.removeEventListener('resize',onResize)
      cancelAnimationFrame(rafRef.current)
      renderer.dispose(); el.removeChild(renderer.domElement)
    }
  },[])

  // ── Animate roll to show result values ──────────────────────────────────
  const animateRoll=useCallback((d1Val:number,d2Val:number)=>{
    const r=rollRef.current
    const d1=dice1Ref.current, d2=dice2Ref.current
    if(!d1||!d2) return

    const [rx1,ry1,rz1]=FACE_ROTATIONS[d1Val]
    const [rx2,ry2,rz2]=FACE_ROTATIONS[d2Val]

    r.d1Start=d1.rotation.clone()
    r.d2Start=d2.rotation.clone()
    r.d1End=new THREE.Euler(rx1,ry1,rz1)
    r.d2End=new THREE.Euler(rx2,ry2,rz2)
    r.d1Bounce=0.8+Math.random()*0.6
    r.d2Bounce=0.6+Math.random()*0.7
    r.t=0; r.dur=1.8; r.active=true
  },[])

  // ── Socket ───────────────────────────────────────────────────────────────
  useEffect(()=>{
    const GS=(import.meta as any).env?.VITE_GAME_SERVER_URL||'http://localhost:3002'
    const s=io(GS,{transports:['polling'],auth:{token}})
    socketRef.current=s
    try{
      const p=JSON.parse(atob(token.split('.')[1]))
      sessionRef.current={playerId:p.sub||'',operatorId:p.operator_id||''}
      if(p.currency) setCurrency(p.currency)
    }catch{}

    s.on('connect',async()=>{
      try{
        const {playerId}=sessionRef.current
        const r=await fetch(`${GS}/wallet/balance?player_id=${playerId}`)
        const d=await r.json(); if(typeof d.balance==='number') setBalance(d.balance)
      }catch{}
    })

    s.on('dice:result',(d:any)=>{
      const {die1,die2,sum,win,payout,balance:newBal}=d
      animateRoll(die1,die2)
      setTimeout(()=>{
        setResult({d1:die1,d2:die2,sum,win,payout})
        if(typeof newBal==='number') setBalance(newBal)
        setPhase('result')
        setHistory(h=>[{sum,win,payout},...h].slice(0,10))
        if(win){
          Sounds.cashout()
          showToast(`🎲 WIN! ₹${payout.toFixed(2)}`,2500)
        } else {
          Sounds.cooked()
          showToast(`💔 ${sum} — better luck next roll!`,2000)
        }
      },1900)
    })

    s.on('dice:error',(d:any)=>{ showToast(`❌ ${d.error}`); setPhase('idle') })
    s.on('balance',(d:any)=>{ if(typeof d.balance==='number') setBalance(d.balance) })
    return()=>{ s.disconnect() }
  },[token,animateRoll])

  // ── Roll ────────────────────────────────────────────────────────────────
  const handleRoll=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||phase==='rolling') return
    if(balance!==null&&balance<stake) return showToast('Not enough balance')
    setBalance(b=>b!==null?Math.max(0,b-stake):b)
    setPhase('rolling'); setResult(null)
    Sounds.go()
    s.emit('dice:roll',{player_id:playerId,operator_id:operatorId,stake,target,bet})
  }

  const STAKES=[5,10,20,50,100,200,500]
  const canRoll=phase==='idle'||phase==='result'

  return(
    <div style={{display:'flex',flexDirection:'column',height:'100%',
      background:'#0a1a0e',color:'#fff',fontFamily:'system-ui,sans-serif',
      userSelect:'none',overflow:'hidden',position:'relative'}}>
      <style>{`
        @keyframes diceToast{0%{opacity:0;transform:translateX(-50%) scale(.85)}15%,85%{opacity:1;transform:translateX(-50%) scale(1)}100%{opacity:0}}
        @keyframes winPop{0%{transform:scale(.8);opacity:0}60%{transform:scale(1.1)}100%{transform:scale(1);opacity:1}}
        .d-btn:hover{filter:brightness(1.1)}.d-btn:active{transform:scale(.96)!important}
        .d-stake:hover{border-color:rgba(255,255,255,.4)!important}
        .d-bet:hover{filter:brightness(1.15)}
      `}</style>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'6px 12px',background:'rgba(0,0,0,.55)',
        borderBottom:'1px solid rgba(255,255,255,.07)',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:16}}>🎲</span>
          <strong style={{fontSize:12,letterSpacing:.6,color:'#e8d5a0'}}>DICE</strong>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <SoundToggleBtn/>
          <div style={{background:'rgba(201,168,76,.1)',border:'1px solid rgba(201,168,76,.3)',
            borderRadius:7,padding:'3px 9px',fontWeight:700,color:'#c9a84c',fontSize:12}}>
            {balance!==null?`₹${fmtCoins(balance)}`:'—'}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast&&(
        <div style={{position:'absolute',top:52,left:'50%',zIndex:60,
          animation:'diceToast 2.5s ease-in-out forwards',
          background:'rgba(0,0,0,.92)',color:'#e8d5a0',padding:'7px 18px',
          borderRadius:20,fontSize:14,fontWeight:800,whiteSpace:'nowrap',
          border:'1px solid rgba(201,168,76,.4)'}}>
          {toast}
        </div>
      )}

      {/* Three.js canvas */}
      <div ref={mountRef} style={{flex:1,position:'relative',overflow:'hidden',minHeight:0}}>
        {/* Result overlay */}
        {phase==='result'&&result&&(
          <div style={{position:'absolute',top:12,left:'50%',transform:'translateX(-50%)',
            zIndex:10,animation:'winPop .4s ease-out',
            background:'rgba(0,0,0,.82)',border:`1px solid ${result.win?'rgba(74,222,128,.5)':'rgba(239,68,68,.4)'}`,
            borderRadius:16,padding:'8px 20px',textAlign:'center',whiteSpace:'nowrap'}}>
            <div style={{fontSize:22,fontWeight:900,
              color:result.win?'#4ade80':'#ef4444'}}>
              {result.d1} + {result.d2} = {result.sum}
            </div>
            <div style={{fontSize:11,color:'rgba(255,255,255,.5)',marginTop:2}}>
              {result.win?`WIN ₹${result.payout.toFixed(2)}`:'No win'}
            </div>
          </div>
        )}

        {/* History dots */}
        <div style={{position:'absolute',bottom:8,left:'50%',transform:'translateX(-50%)',
          display:'flex',gap:4,zIndex:10}}>
          {history.slice(0,8).map((h,i)=>(
            <div key={i} title={`Sum ${h.sum}`} style={{
              width:10,height:10,borderRadius:'50%',
              background:h.win?'#4ade80':'#ef4444',
              opacity:1-i*0.1,
            }}/>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div style={{background:'linear-gradient(to top,rgba(0,0,0,.97),rgba(0,0,0,.7))',
        borderTop:'1px solid rgba(255,255,255,.06)',
        padding:'8px 10px 12px',flexShrink:0}}>

        {/* Target selector */}
        <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:7}}>
          <span style={{fontSize:9,color:'rgba(255,255,255,.35)',whiteSpace:'nowrap'}}>TARGET:</span>
          <div style={{display:'flex',gap:3,flex:1,justifyContent:'center'}}>
            {TARGETS.map(t=>(
              <button key={t} className="d-stake"
                onClick={()=>canRoll&&setTarget(t)}
                style={{
                  borderRadius:5,padding:'4px 8px',cursor:canRoll?'pointer':'default',
                  border:target===t?'1.5px solid #e8d5a0':'1.5px solid rgba(255,255,255,.1)',
                  background:target===t?'rgba(232,213,160,.18)':'rgba(255,255,255,.04)',
                  color:target===t?'#e8d5a0':'rgba(255,255,255,.4)',
                  fontSize:12,fontWeight:800,transition:'all .1s',
                }}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Over / Under */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:7}}>
          {(['over','under'] as Bet[]).map(b=>{
            const m=b==='over'?OVER_MULT[target]:UNDER_MULT[target]
            const active=bet===b
            return(
              <button key={b} className="d-bet"
                onClick={()=>canRoll&&setBet(b)}
                style={{
                  borderRadius:8,padding:'8px 4px',cursor:canRoll?'pointer':'default',
                  border:active?`2px solid ${b==='over'?'#4ade80':'#60a5fa'}`:'2px solid rgba(255,255,255,.1)',
                  background:active
                    ? b==='over'?'rgba(74,222,128,.15)':'rgba(96,165,250,.15)'
                    : 'rgba(255,255,255,.04)',
                  color:active
                    ? b==='over'?'#4ade80':'#60a5fa'
                    : 'rgba(255,255,255,.35)',
                  transition:'all .15s',textAlign:'center',
                }}>
                <div style={{fontSize:13,fontWeight:900}}>
                  {b==='over'?`OVER ${target}`:`UNDER ${target}`}
                </div>
                <div style={{fontSize:10,opacity:.8,marginTop:2}}>{m}× payout</div>
              </button>
            )
          })}
        </div>

        {/* Stake + Roll */}
        <div style={{display:'flex',gap:5,alignItems:'center',marginBottom:7}}>
          <span style={{fontSize:9,color:'rgba(255,255,255,.35)',whiteSpace:'nowrap'}}>STAKE:</span>
          <div style={{display:'flex',gap:3,overflowX:'auto',flex:1}}>
            {STAKES.map(sv=>(
              <button key={sv} className="d-stake"
                onClick={()=>canRoll&&setStake(sv)}
                style={{
                  borderRadius:5,padding:'3px 7px',cursor:canRoll?'pointer':'default',
                  border:stake===sv?'1.5px solid #e8d5a0':'1.5px solid rgba(255,255,255,.1)',
                  background:stake===sv?'rgba(232,213,160,.18)':'rgba(255,255,255,.04)',
                  color:stake===sv?'#e8d5a0':'rgba(255,255,255,.4)',
                  fontSize:10,fontWeight:700,whiteSpace:'nowrap',flexShrink:0,transition:'all .1s',
                }}>
                ₹{sv>=1000?`${sv/1000}K`:sv}
              </button>
            ))}
          </div>
        </div>

        {/* Roll button */}
        <button className="d-btn" onClick={handleRoll} disabled={!canRoll}
          style={{
            width:'100%',padding:'12px 0',borderRadius:10,border:'none',
            cursor:canRoll?'pointer':'default',fontWeight:900,fontSize:16,
            background:canRoll
              ?'linear-gradient(135deg,#c8873a,#8b5a1a)'
              :'rgba(255,255,255,.08)',
            color:canRoll?'#fff':'rgba(255,255,255,.2)',
            transition:'all .15s',
            boxShadow:canRoll?'0 4px 20px rgba(200,135,58,.4)':'none',
          }}>
          {phase==='rolling'?'🎲 Rolling...':'🎲 ROLL DICE'}
        </button>

        {/* Info row */}
        <div style={{display:'flex',justifyContent:'space-between',marginTop:5,
          fontSize:9,color:'rgba(255,255,255,.25)'}}>
          <span>Win if sum {bet==='over'?`> ${target}`:`< ${target}`}</span>
          <span>{mult}× · ₹{(stake*(mult-1)).toFixed(1)} profit</span>
        </div>
      </div>
    </div>
  )
}
